/**
 * The note People starts from when the host writes to a whole group: the words differ by what the group did, and
 * `{{first name}}` becomes each person's first name when it goes. The app fills the sheet with these; the server
 * falls back to them when AI has nothing to offer.
 */

/** What the people in a group did, as far as the note is concerned. */
export type NoteAbout = "quiet" | "missed" | "nosign" | "any";

export const NOTE_SUBJECT_MAX = 200;
export const NOTE_TEXT_MAX = 5000;

const WORDS: Record<NoteAbout, { subject: string; line: string }> = {
  quiet: {
    subject: "Pick up where you left off?",
    line: "You started one of my talks a while ago. Your page keeps your place, so you can pick it up whenever it suits you.",
  },
  missed: {
    subject: "Sorry we missed you",
    line: "You registered but didn't get the chance to watch. The replay is on your page whenever you're ready, and it keeps your place if you stop.",
  },
  nosign: {
    subject: "Still thinking it over?",
    line: "I saw you looked at the offer. If the times didn't suit you, just reply with a day that does and I'll make it work.",
  },
  any: {
    subject: "A quick note",
    line: "Thanks for joining one of my talks. If anything came up while you watched, just reply here. I read every message myself.",
  },
};

export function noteAboutOf(stage: string | undefined): NoteAbout {
  return stage === "quiet" || stage === "missed" || stage === "nosign" ? stage : "any";
}

/** The note a group starts from, signed with the host's first name. */
export function startingNote(about: NoteAbout, hostFirstName: string): { subject: string; text: string } {
  const w = WORDS[about];
  const sign = hostFirstName.trim();
  return {
    subject: about === "any" && sign ? `A quick note from ${sign}` : w.subject,
    text: `Hi {{first name}},\n\n${w.line}${sign ? `\n\n${sign}` : ""}`,
  };
}
